import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AnnouncementType = 'banner' | 'event' | 'update' | 'tip';

export interface Announcement {
  id: string;
  title: string;
  content: string | null;
  type: AnnouncementType;
  cta_text: string | null;
  cta_url: string | null;
  event_date: string | null;
  starts_at: string;
  ends_at: string | null;
  target_audience: string[] | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface AnnouncementDismissal {
  id: string;
  announcement_id: string;
  user_id: string;
  dismissed_at: string;
}

// Check if an event is imminent (within 15 minutes)
const isEventImminent = (eventDate: string | null): boolean => {
  if (!eventDate) return false;
  const date = new Date(eventDate);
  const now = new Date();
  const minutesUntilEvent = (date.getTime() - now.getTime()) / (1000 * 60);
  return minutesUntilEvent <= 15 && minutesUntilEvent >= -60;
};

/**
 * Fetch active announcements the user hasn't dismissed
 * For events: re-show dismissed banners when event becomes imminent (within 15 min)
 */
export const useActiveAnnouncements = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["active-announcements", user?.id],
    queryFn: async () => {
      // Fetch active announcements
      const { data: announcements, error: announcementsError } = await supabase
        .from("announcements")
        .select("*")
        .eq("is_active", true)
        .lte("starts_at", new Date().toISOString())
        .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`)
        .order("created_at", { ascending: false });

      if (announcementsError) throw announcementsError;
      if (!announcements?.length) return [];

      // If user is logged in, filter out dismissed ones (with smart re-appearance for events)
      if (user?.id) {
        const { data: dismissals, error: dismissalsError } = await supabase
          .from("announcement_dismissals")
          .select("announcement_id, dismissed_at")
          .eq("user_id", user.id);

        if (dismissalsError) throw dismissalsError;

        const now = new Date();
        
        // Build set of announcements that should remain dismissed
        const dismissedIds = new Set(
          dismissals?.filter((d) => {
            const announcement = (announcements as Announcement[]).find(a => a.id === d.announcement_id);
            
            // For event-type announcements, check if they should resurface
            if (announcement?.type === 'event' && announcement.event_date) {
              const eventIsImminent = isEventImminent(announcement.event_date);
              const dismissedAt = new Date(d.dismissed_at);
              const minutesSinceDismissal = (now.getTime() - dismissedAt.getTime()) / (1000 * 60);
              
              // If event is imminent AND was dismissed more than 15 minutes ago, show again
              if (eventIsImminent && minutesSinceDismissal > 15) {
                return false; // Don't include in dismissed set - show it again
              }
            }
            
            return true; // Keep in dismissed set
          }).map(d => d.announcement_id) || []
        );
        
        return (announcements as Announcement[]).filter(
          (a) => !dismissedIds.has(a.id)
        );
      }

      return announcements as Announcement[];
    },
    staleTime: 1000 * 60, // 1 minute for more responsive re-appearance
    enabled: true,
  });
};

/**
 * Dismiss an announcement for the current user
 */
export const useDismissAnnouncement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (announcementId: string) => {
      if (!user?.id) {
        // For non-logged in users, just dismiss locally (will show again next session)
        return { id: announcementId };
      }

      const { data, error } = await supabase
        .from("announcement_dismissals")
        .insert({
          announcement_id: announcementId,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (announcementId) => {
      // Optimistically remove the announcement from the list
      await queryClient.cancelQueries({ queryKey: ["active-announcements"] });
      
      const previousAnnouncements = queryClient.getQueryData<Announcement[]>([
        "active-announcements",
        user?.id,
      ]);

      queryClient.setQueryData<Announcement[]>(
        ["active-announcements", user?.id],
        (old) => old?.filter((a) => a.id !== announcementId) || []
      );

      return { previousAnnouncements };
    },
    onError: (err, announcementId, context) => {
      // Rollback on error
      if (context?.previousAnnouncements) {
        queryClient.setQueryData(
          ["active-announcements", user?.id],
          context.previousAnnouncements
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["active-announcements"] });
    },
  });
};

/**
 * Admin: Fetch all announcements
 */
export const useAllAnnouncements = () => {
  return useQuery({
    queryKey: ["all-announcements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Announcement[];
    },
  });
};

/**
 * Admin: Create a new announcement
 */
export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      announcement: Omit<Announcement, "id" | "created_at" | "updated_at" | "created_by">
    ) => {
      const { data, error } = await supabase
        .from("announcements")
        .insert({
          ...announcement,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Announcement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-announcements"] });
      queryClient.invalidateQueries({ queryKey: ["active-announcements"] });
    },
  });
};

/**
 * Admin: Update an announcement
 */
export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Omit<Announcement, "id" | "created_at" | "updated_at" | "created_by">>;
    }) => {
      const { data, error } = await supabase
        .from("announcements")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Announcement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-announcements"] });
      queryClient.invalidateQueries({ queryKey: ["active-announcements"] });
    },
  });
};

/**
 * Admin: Delete an announcement
 */
export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("announcements")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-announcements"] });
      queryClient.invalidateQueries({ queryKey: ["active-announcements"] });
    },
  });
};

/**
 * Admin: Get dismissal stats for an announcement
 */
export const useAnnouncementStats = (announcementId: string) => {
  return useQuery({
    queryKey: ["announcement-stats", announcementId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("announcement_dismissals")
        .select("*", { count: "exact", head: true })
        .eq("announcement_id", announcementId);

      if (error) throw error;
      return { dismissalCount: count || 0 };
    },
    enabled: !!announcementId,
  });
};
